import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, Subscription, throwError } from 'rxjs';
import mime from 'mime';
import { TranslateService } from '@ngx-translate/core';
import { logger } from '@src/utils';
import { AlertService } from '@src/core/services';
import { APP_CONFIG } from '@src/core';
import { AuthUserService } from '@src/app/modules/auth';
import { UserService } from '@src/api';
import { environment } from '@src/environments/environment';

import {
  AndroidPermissions,
  DeviceModel,
  EventForCalendar,
  ICordovaPluginAndroidPermissions,
  PlatformEnum,
} from '../types';

let self: any;

@Injectable({
  providedIn: 'root',
})
export class CordovaService {
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly authUserService = inject(AuthUserService);
  private readonly api = inject(UserService);

  cordova?: any;

  permissionsPlugin?: ICordovaPluginAndroidPermissions;

  androidRecordAudioPermissions = [AndroidPermissions.RECORD_AUDIO, AndroidPermissions.MODIFY_AUDIO_SETTINGS];
  androidCalendarPermissions = [AndroidPermissions.READ_CALENDAR, AndroidPermissions.WRITE_CALENDAR];
  androidCameraPermissions = [AndroidPermissions.CAMERA];
  androidExternalStoragePermissions = [
    AndroidPermissions.WRITE_EXTERNAL_STORAGE,
    AndroidPermissions.READ_EXTERNAL_STORAGE,
  ];

  androidAudioFilesPermissions = [AndroidPermissions.READ_MEDIA_AUDIO];
  androidNotificationsPermissions = [AndroidPermissions.POST_NOTIFICATIONS];

  requiredAndroidPermissions: AndroidPermissions[] = [
    ...this.androidRecordAudioPermissions,
    ...this.androidCalendarPermissions,
    ...this.androidCameraPermissions,
    ...this.androidExternalStoragePermissions,
    ...this.androidNotificationsPermissions,
  ];

  fileOpener2Plugin?: any;
  calendarPlugin?: any;
  universalLinksPlugin?: any;
  firebasePlugin?: any;
  devicePlugin?: DeviceModel;

  readonly fcmToken$ = new BehaviorSubject<string | undefined>(undefined);

  constructor() {
    logger('CordovaService - constructor');
    this.init();
  }

  get isCordova() {
    return this.cordova !== undefined;
  }

  get isAndroid() {
    return this.devicePlugin?.platform.toLowerCase() === PlatformEnum.android.toLowerCase();
  }

  get isIos() {
    return this.devicePlugin?.platform.toLowerCase() === PlatformEnum.ios.toLowerCase();
  }

  async init() {
    logger('CordovaService - init');
    self = this;
    this.cordova = (window as any).cordova;

    if (!this.cordova) {
      logger('cordova не подключена');
      return;
    }

    this.fileOpener2Plugin = this.cordova?.plugins?.fileOpener2;
    this.calendarPlugin = (window as any).plugins?.calendar;
    this.devicePlugin = (window as any).device;

    this.permissionsPlugin = this.cordova?.plugins?.permissions;

    this.universalLinksPlugin = (window as any).plugins?.universalLinks;
    if (this.universalLinksPlugin) {
      this.initUniversalLinks();
    }

    this.firebasePlugin = (window as any).FirebasePlugin;
    if (this.firebasePlugin) {
      this.checkIOSNotificationPermission(true);

      let subscription: Subscription | undefined;
      this.firebasePlugin.onTokenRefresh(
        (fcmToken: string) => {
          this.fcmToken$.next(fcmToken);
          this.pushSubscribe();
          subscription = this.authUserService.authUser$.subscribe(authUser => {
            if (authUser) {
              this.registerDevice(fcmToken);
              subscription?.unsubscribe();
            }
          });
        },
        (error: any) => {
          logger('onTokenRefresh - error', error);
        },
      );
    }

    document.addEventListener(
      'pause',
      function () {
        logger('Приложение свернуто (ушло в фон)');
      },
      false,
    );

    document.addEventListener(
      'resume',
      function () {
        logger('Приложение активировано (вернулось в передний план)');
      },
      false,
    );
  }

  async isAccessToRecordAudio() {
    if (this.isIos) {
      return true;
    }

    return await this.checkAndroidPermissions(this.androidRecordAudioPermissions);
  }

  async isAccessToCalendar() {
    if (this.isIos) {
      return true;
    }

    const res = await this.checkAndroidPermissions(this.androidCalendarPermissions);
    if (!res) {
      this.alertService.warning(this.translateService.instant('common.alerts.warnings.accessToCalendar'));
    }

    return res;
  }

  async isAccessToCamera() {
    if (this.isIos) {
      return true;
    }

    return await this.checkAndroidPermissions(this.androidCameraPermissions);
  }

  async isAccessToExternalStorage() {
    if (this.isIos) {
      return true;
    }

    return await this.checkAndroidPermissions(this.androidExternalStoragePermissions);
  }

  async isAccessToAudioFiles() {
    if (this.isIos) {
      return true;
    }

    const res = await this.checkAndroidPermissions(this.androidAudioFilesPermissions);
    if (!res) {
      this.alertService.warning(this.translateService.instant('common.alerts.warnings.accessToAudioFiles'));
    }

    return res;
  }

  async isAccessToNotifications() {
    if (this.isIos) {
      return await this.checkIOSNotificationPermission(false);
    } else {
      return await this.checkAndroidPermissions(this.androidNotificationsPermissions);
    }
  }

  checkAndroidPermissions(permissions: AndroidPermissions[]): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (!this.permissionsPlugin) return resolve(false);

      this.permissionsPlugin.requestPermissions(
        permissions,
        res => {
          logger('checkAndroidPermissions', res);
          return resolve(res.hasPermission ?? false);
        },
        err => {
          logger('checkAndroidPermissions - error', err);
          return resolve(false);
        },
      );
    });
  }

  checkRequiredAndroidPermissions() {
    if (!this.permissionsPlugin) return;

    this.permissionsPlugin.requestPermissions(
      this.requiredAndroidPermissions,
      res => {
        logger('checkRequiredAndroidPermissions', res);
      },
      err => {
        logger('checkRequiredAndroidPermissions - error', err);
      },
    );
  }

  checkPermission(permission: AndroidPermissions): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (!this.permissionsPlugin) return resolve(false);

      this.permissionsPlugin.checkPermission(
        permission,
        res => {
          return resolve(res.hasPermission ?? false);
        },
        err => {
          logger('checkPermission - error', err);
          return resolve(false);
        },
      );
    });
  }

  requestPermission(permission: AndroidPermissions): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (!this.permissionsPlugin) return resolve(false);

      this.permissionsPlugin.requestPermission(
        permission,
        res => {
          return resolve(res.hasPermission ?? false);
        },
        err => {
          logger('requestPermission - error', err);
          return resolve(false);
        },
      );
    });
  }

  downloadFile(blob: Blob | string, fileName: string) {
    if (!blob || !fileName) return;

    // HACK: !ВАЖНО не должно содержать знаки (:), иначе не работает в Cordova
    fileName = fileName.replace(/:/g, '.');

    // INFO: Описание всех папок для Android и iOS https://github.com/apache/cordova-plugin-file#file-system-layouts
    const fileDir = self.cordova.file.dataDirectory;

    (window as any).resolveLocalFileSystemURL(
      fileDir,
      function (fs: any) {
        logger(`$ file system opened directory ${fileDir}`);
        logger(`$ opening ${fileName}`);
        fs.getFile(
          fileName,
          { create: true, exclusive: false },
          function (fileEntry: any) {
            logger('getFile - ok!');
            self.writeFile(fileEntry, blob);
          },
          function (err: any) {
            logger('getFile - error', err);
          },
        );
      },
      function (err: any) {
        logger('$ resolveLocalFileSystemURL err', err);
      },
    );
  }

  writeFile(fileEntry: any, blob: Blob | string) {
    const fileName = fileEntry.name;
    logger('fileName', fileName);
    const fileMIMEType = mime.getType(fileName) ?? (typeof blob !== 'string' ? blob.type : 'application/pdf');
    logger('fileMIMEType', fileMIMEType);

    fileEntry.createWriter(function (fileWriter: any) {
      fileWriter.onwriteend = function () {
        logger('fileWriter - ok!');
        self.alertService.success(
          `${self.translateService.instant('common.labels.file')} ${fileName} ${self.translateService.instant(
            'common.labels.uploaded',
          )}`,
        );
        self.openFile(fileEntry.nativeURL, fileMIMEType);
      };
      fileWriter.onerror = function (err: any) {
        logger('fileWriter - error', err);
      };
      fileWriter.write(blob);
    });
  }

  openURL(url: string, baseUrl: boolean = false) {
    this.cordova.InAppBrowser.open(
      baseUrl ? `${APP_CONFIG.baseUrl}/${url}` : url,
      baseUrl && this.isIos ? '_blank' : '_system',
    );
  }

  openFile(filePath: string, fileMIMEType: string) {
    if (!this.fileOpener2Plugin) {
      logger('Ошибка! Плагин fileOpener2 для Cordova не подключен');
      return;
    }

    this.fileOpener2Plugin.open(filePath, fileMIMEType, {
      error: function (err: any) {
        logger('Error status: ' + err.status + ' - Error message: ' + err.message);
      },
      success: function () {
        logger('file opened successfully');
      },
    });
  }

  async addEventToCalendar({
    title = '',
    eventLocation = '',
    notes = '',
    startDate = null,
    endDate = null,
    onlineLink = undefined,
  }: EventForCalendar) {
    if (!(await this.isAccessToCalendar())) {
      return;
    }

    const calOptions = this.calendarPlugin.getCalendarOptions();
    if (onlineLink) {
      calOptions.url = onlineLink;
    }

    this.calendarPlugin.createEventInteractivelyWithOptions(
      title,
      eventLocation,
      notes,
      startDate,
      endDate,
      calOptions,
      () => {
        this.alertService.success(this.translateService.instant('common.alerts.successes.addEventToCalendar'));
      },
      (error: any) => {
        this.alertService.error(this.translateService.instant('common.alerts.errors.addEventToCalendar'));
        logger(this.translateService.instant('common.alerts.errors.addEventToCalendar'), error);
      },
    );
  }

  initUniversalLinks() {
    if (!this.universalLinksPlugin) {
      logger('Ошибка! Плагин universalLinks для Cordova не подключен');
      return;
    }

    this.universalLinksPlugin.subscribe(null, (eventData: any) => {
      this.zone.run(() => {
        this.authUserService.authUser$.subscribe(authUser => {
          if (authUser) {
            this.router
              .navigate([eventData.path], {
                queryParams: eventData.params,
              })
              .then();
          }
        });
      });
    });
  }

  checkIOSNotificationPermission(requested: boolean) {
    return new Promise<boolean>(resolve => {
      this.firebasePlugin?.hasPermission(
        async (hasPermission: boolean) => {
          logger('checkIOSNotificationPermission', hasPermission);
          if (hasPermission) {
            // Granted
            return resolve(hasPermission);
          } else if (!requested) {
            // Request permission
            this.grantPermission().then(res => {
              return resolve(res);
            });
          } else {
            // Denied
            logger('Разрешение на push-уведомления не предоставлено');
            return resolve(false);
          }
        },
        (error: string) => {
          logger('hasPermission - error', error);
        },
      );
    });
  }

  grantPermission() {
    return new Promise<boolean>(resolve => {
      if (!this.firebasePlugin) {
        logger('Ошибка! Плагин firebasePlugin для Cordova не подключен');
        return resolve(false);
      }

      this.firebasePlugin.grantPermission(
        async (hasPermission: boolean) => {
          logger('grantPermission', hasPermission);
          return resolve(hasPermission);
        },
        (error: string) => {
          logger('grantPermission - error', error);
          return resolve(false);
        },
        (requestWithProvidesAppNotificationSettings: boolean) => {
          logger(
            'grantPermission - requestWithProvidesAppNotificationSettings',
            requestWithProvidesAppNotificationSettings,
          );
          return resolve(requestWithProvidesAppNotificationSettings);
        },
      );
    });
  }

  pushSubscribe() {
    let subscription: Subscription | undefined;

    this.firebasePlugin?.onMessageReceived(
      (message: any) => {
        logger('onMessageReceived - message', message);
        if (message.messageType === 'notification') {
          // Чтобы уведомление отображалось когда приложение на переднем плане, нужно добавить "notification_foreground": "true" в data
          // Если приложение закрыто, то уведомление отображается, но при нажатии переход по ссылке не работает
          // (причина - приложение не успевает загрузиться. добавил ожидание загрузки данных текущего пользователя)
          if (message.tap) {
            if (message.url) {
              this.zone.run(() => {
                subscription = this.authUserService.authUser$.subscribe(authUser => {
                  if (authUser) {
                    this.router.navigateByUrl(message.url).then(() => {
                      subscription?.unsubscribe();
                    });
                  }
                });
              });
            }
          }
        }
      },
      (error: any) => {
        logger('onMessageReceived - error', error);
      },
    );
  }

  registerDevice(fcmToken: string) {
    if (!this.devicePlugin) {
      logger('Ошибка! Плагин device для Cordova не подключен');
      return;
    }

    this.api
      .registerDeviceId({
        deviceId: fcmToken,
        osType: this.devicePlugin.platform,
        osVersion: this.devicePlugin.version,
        languageCode: this.translateService.currentLang,
        unionsVersion: environment.appVersion,
      })
      .pipe(
        catchError(err => {
          logger('registerDeviceId - error', err);
          return throwError(() => err);
        }),
      )
      .subscribe();
  }
}
